import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { listModels } from 'api/models/listModels';
import { queryKeys } from 'utils/react-query';
import { IconTrendUp } from 'components/Icons';

type ModelsList = {
  options: CF.API.Models.Model[];
  title: { icon?: React.ReactNode; text: string };
};

type UseModelsListProps = {
  userOrOrgId?: string;
  searchParam: string;
};

type UseModelsListResult = {
  data: ModelsList[];
  loading: boolean;
};

const PAGES_OF_RESULTS = 1;
const RESULTS_PER_PAGE = 20;
const paginationQueryParam = `page=${PAGES_OF_RESULTS}&per_page=${RESULTS_PER_PAGE}`;
const additionalModelFieldsQueryParam = '&additional_fields=stars&additional_fields=presets';

export function useModelsList({ userOrOrgId, searchParam }: UseModelsListProps): UseModelsListResult {
  const searchQueryParam = searchParam.length ? `&search=${searchParam}` : '';

  const { data: featuredModelsData, isLoading: featuredIsLoading } = useQuery({
    queryKey: [queryKeys.UniversalSearch, 'Featured', { searchQueryParam }],
    queryFn: () => listModels({ params: `${paginationQueryParam}${additionalModelFieldsQueryParam}&featured_only=true${searchQueryParam}` }),
  });

  const { data: communityModelsData, isLoading: communityIsLoading } = useQuery({
    queryKey: [queryKeys.UniversalSearch, 'Community', { searchQueryParam }],
    queryFn: () => listModels({ params: `${paginationQueryParam}${additionalModelFieldsQueryParam}${searchQueryParam}` }),
    enabled: !userOrOrgId,
  });

  const { data: modelsData, isLoading: modelsIsLoading } = useQuery({
    queryKey: [queryKeys.UniversalSearch, 'Results', { searchQueryParam, userOrOrgId }],
    queryFn: () => listModels({ userId: userOrOrgId, params: `${paginationQueryParam}${additionalModelFieldsQueryParam}${searchQueryParam}` }),
    enabled: Boolean(userOrOrgId),
  });

  const data: ModelsList[] = useMemo(() => {
    const lists = [];

    if (featuredModelsData?.models.length && !userOrOrgId && !searchParam) {
      lists.push({
        title: { icon: <IconTrendUp size={20} />, text: 'Trending models' },
        options: featuredModelsData?.models,
      });
    }

    if (communityModelsData?.models.length && !userOrOrgId) {
      lists.push({
        title: { text: 'Community' },
        options: communityModelsData?.models,
      });
    }

    if ((modelsData?.models.length || featuredModelsData?.models.length) && userOrOrgId) {
      lists.push({
        title: { text: 'Search results' },
        options: [...(featuredModelsData?.models || []), ...(modelsData?.models || [])],
      });
    }

    return lists;
  }, [featuredModelsData?.models, communityModelsData?.models, modelsData?.models, userOrOrgId, searchParam]);

  return { data, loading: featuredIsLoading || communityIsLoading || modelsIsLoading };
}
